<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KnowledgebasesAPI from 'dashboard/api/knowledgebases';

const props = defineProps({
  modelValue: { type: String, default: '' },
  showGallery: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const dialogRef = ref(null);
const fileInput = ref(null);
const uploading = ref(false);
const dragOver = ref(false);
const draftUrl = ref('');
const uploadError = ref('');

const galleries = ref([]);
const galleriesLoading = ref(false);
const selectedGalleryId = ref(null);
const images = ref([]);
const imagesLoading = ref(false);

const filteredImages = computed(() => images.value);

const canSave = computed(
  () => !!draftUrl.value && draftUrl.value !== props.modelValue
);

const hasImage = computed(() => !!props.modelValue);

async function loadGalleries() {
  if (!props.showGallery) return;
  galleriesLoading.value = true;
  try {
    const response = await KnowledgebasesAPI.get();
    galleries.value = (response.data || []).filter(kb => kb.type === 'gallery');
  } catch {
    galleries.value = [];
  } finally {
    galleriesLoading.value = false;
  }
}

async function loadImages(galleryId) {
  if (!galleryId) {
    images.value = [];
    return;
  }
  imagesLoading.value = true;
  try {
    const response = await KnowledgebasesAPI.getRecords(galleryId, {
      per_page: 100,
    });
    images.value = (response.data.records || [])
      .filter(r => r.file?.url)
      .map(r => ({
        id: r.id,
        url: r.file.web_url || r.file.url,
        name: r.name,
      }));
  } catch {
    images.value = [];
  } finally {
    imagesLoading.value = false;
  }
}

watch(selectedGalleryId, id => loadImages(id));

watch(galleries, list => {
  if (list.length && !selectedGalleryId.value) {
    selectedGalleryId.value = list[0].id;
  }
});

const openDialog = () => {
  draftUrl.value = props.modelValue || '';
  uploadError.value = '';
  dialogRef.value?.open();
  if (props.showGallery && !galleries.value.length) loadGalleries();
};

const clearImage = () => {
  emit('update:modelValue', '');
  draftUrl.value = '';
};

const selectGalleryImage = url => {
  draftUrl.value = url;
  uploadError.value = '';
};

const cancelDialog = () => {
  draftUrl.value = props.modelValue || '';
  uploadError.value = '';
  dialogRef.value?.close();
};

const saveImage = () => {
  if (!draftUrl.value) return;
  emit('update:modelValue', draftUrl.value);
  dialogRef.value?.close();
};

const validateFile = file => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    uploadError.value = t('IMAGE_INPUT.INVALID_TYPE');
    return false;
  }
  if (file.size > MAX_SIZE_BYTES) {
    uploadError.value = t('IMAGE_INPUT.TOO_LARGE');
    return false;
  }
  return true;
};

const processFile = async file => {
  if (!file || !validateFile(file)) return;

  uploading.value = true;
  uploadError.value = '';
  try {
    const { fileUrl, webUrl } = await uploadFile(file);
    draftUrl.value = webUrl || fileUrl;
  } catch {
    uploadError.value = t('IMAGE_INPUT.UPLOAD_FAILED');
  } finally {
    uploading.value = false;
  }
};

const onFileChange = async event => {
  const [file] = event.target.files || [];
  await processFile(file);
  event.target.value = null;
};

const onDrop = async event => {
  dragOver.value = false;
  const [file] = event.dataTransfer?.files || [];
  await processFile(file);
};

defineExpose({ openDialog, clearImage });

onMounted(() => {
  if (props.showGallery) loadGalleries();
});
</script>

<template>
  <div class="flex items-center gap-2 min-w-0 w-full">
    <button
      type="button"
      class="flex items-center justify-center size-8 shrink-0 rounded-md overflow-hidden border border-n-slate-5 bg-n-slate-2 focus:outline-none focus:ring-1 focus:ring-n-brand-7"
      :title="t('IMAGE_INPUT.CHANGE')"
      :disabled="uploading"
      @click="openDialog"
    >
      <img
        v-if="modelValue"
        :src="modelValue"
        alt=""
        class="size-full object-cover"
      />
      <span v-else class="i-lucide-image size-3.5 text-n-slate-8" />
    </button>

    <button
      v-if="hasImage"
      type="button"
      class="shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-full bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 transition-colors"
      @click="clearImage"
    >
      {{ t('IMAGE_INPUT.CLEAR') }}
    </button>
    <button
      v-else
      type="button"
      class="shrink-0 px-2.5 py-1 text-[11px] font-medium rounded-full bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 transition-colors disabled:opacity-50"
      :disabled="uploading"
      @click="openDialog"
    >
      <span v-if="uploading" class="inline-flex items-center gap-1">
        <span class="i-lucide-loader-circle size-3 animate-spin" />
        {{ t('IMAGE_INPUT.UPLOAD') }}
      </span>
      <span v-else>{{ t('IMAGE_INPUT.UPLOAD') }}</span>
    </button>
  </div>

  <Dialog
    ref="dialogRef"
    :title="t('IMAGE_INPUT.DIALOG_TITLE')"
    :description="t('IMAGE_INPUT.DIALOG_DESCRIPTION')"
    width="lg"
    position="top"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
  >
    <div class="flex flex-col gap-5">
      <div class="space-y-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('IMAGE_INPUT.UPLOAD_LABEL') }}
        </p>

        <div
          v-if="draftUrl"
          class="relative w-full overflow-hidden rounded-xl border border-n-slate-5 bg-n-slate-1"
        >
          <img
            :src="draftUrl"
            alt=""
            class="w-full max-h-56 object-contain bg-n-slate-2"
          />
          <div
            class="flex items-center justify-between gap-2 px-3 py-2 border-t border-n-slate-5"
          >
            <p class="text-xs text-n-slate-11 truncate">
              {{ t('IMAGE_INPUT.SELECTED_PREVIEW') }}
            </p>
            <button
              type="button"
              class="shrink-0 text-xs font-medium text-n-brand-11 hover:text-n-brand-12 transition-colors disabled:opacity-50"
              :disabled="uploading"
              @click="fileInput?.click()"
            >
              {{ t('IMAGE_INPUT.REPLACE') }}
            </button>
          </div>
        </div>

        <button
          v-else
          type="button"
          class="flex flex-col items-center justify-center w-full gap-2 px-4 py-8 rounded-xl border border-dashed transition-colors text-center"
          :class="
            dragOver
              ? 'border-n-brand-7 bg-n-brand-2'
              : 'border-n-slate-6 bg-n-slate-1 hover:border-n-slate-8 hover:bg-n-slate-2'
          "
          :disabled="uploading"
          @click="fileInput?.click()"
          @dragenter.prevent="dragOver = true"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
        >
          <span
            class="flex items-center justify-center size-10 rounded-full bg-n-slate-3 text-n-slate-11"
          >
            <span
              :class="
                uploading
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-folder-up'
              "
              class="size-5"
            />
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('IMAGE_INPUT.DROPZONE_TITLE') }}
          </span>
          <span class="text-xs text-n-slate-9">
            {{ t('IMAGE_INPUT.DROPZONE_HINT') }}
          </span>
        </button>

        <p v-if="uploadError" class="text-xs text-n-ruby-11">
          {{ uploadError }}
        </p>
      </div>

      <template v-if="showGallery">
        <div class="flex items-center gap-3">
          <div class="flex-1 h-px bg-n-slate-5" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
          >
            {{ t('IMAGE_INPUT.OR') }}
          </span>
          <div class="flex-1 h-px bg-n-slate-5" />
        </div>

        <div class="space-y-2">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('IMAGE_INPUT.GALLERY_LABEL') }}
          </p>

          <div
            v-if="galleries.length > 1"
            class="flex gap-1 overflow-x-auto pb-1"
          >
            <button
              v-for="gallery in galleries"
              :key="gallery.id"
              type="button"
              class="shrink-0 px-2.5 py-1 text-xs rounded-full border transition-colors"
              :class="
                selectedGalleryId === gallery.id
                  ? 'border-n-brand-7 bg-n-brand-2 text-n-brand-11'
                  : 'border-n-slate-5 text-n-slate-11 hover:bg-n-slate-3'
              "
              @click="selectedGalleryId = gallery.id"
            >
              {{ gallery.name }}
            </button>
          </div>

          <div
            class="rounded-xl border border-n-slate-5 bg-n-slate-1 p-2 min-h-[5.5rem]"
          >
            <div
              v-if="galleriesLoading || imagesLoading"
              class="flex items-center justify-center h-20 text-xs text-n-slate-9"
            >
              {{ t('IMAGE_INPUT.LOADING') }}
            </div>
            <div
              v-else-if="filteredImages.length"
              class="flex gap-2 overflow-x-auto pb-1"
            >
              <button
                v-for="img in filteredImages"
                :key="img.id"
                type="button"
                class="relative shrink-0 size-16 rounded-lg overflow-hidden border-2 transition-all"
                :class="
                  draftUrl === img.url
                    ? 'border-n-brand-7 ring-1 ring-n-brand-7'
                    : 'border-n-slate-5 hover:border-n-slate-8'
                "
                :title="img.name"
                @click="selectGalleryImage(img.url)"
              >
                <img
                  :src="img.url"
                  :alt="img.name"
                  class="size-full object-cover"
                />
              </button>
            </div>
            <div
              v-else
              class="flex items-center justify-center h-20 text-xs text-n-slate-9"
            >
              {{ t('IMAGE_INPUT.GALLERY_EMPTY') }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2 w-full">
        <Button
          type="button"
          color="slate"
          variant="faded"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="cancelDialog"
        />
        <Button
          type="button"
          color="blue"
          :label="t('IMAGE_INPUT.SAVE')"
          :disabled="!canSave || uploading"
          @click="saveImage"
        />
      </div>
    </template>
  </Dialog>

  <input
    ref="fileInput"
    type="file"
    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
    class="hidden"
    @change="onFileChange"
  />
</template>
